import type { PropsWithChildren } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { NavWrapper } from 'components/OfferPage/styles'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { FormattedMessage, useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import {
  arePricesLoading,
  getFromDurationLabel,
  getCheapestSelectedPackageOptionWithDefault,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getMultiRoomPackageTotals, type MultiRoomPackageTotals } from 'selectors/offerPage/multipleRoomBookingSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { trackOfferPageTopCtaClick } from 'components/OfferPage/analytics'
import WalledContent from 'components/Common/WalledContent'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import Group from 'components/utils/Group'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import { addFlightPrice } from 'lib/offer/pricing'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { getPriceDisplayFunction, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import FlashHotelWePriceMatch from './FlashHotelWePriceMatch'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { isRewardFareClass, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { computeRewardBundlePrice, getRewardPriceDisplayState } from 'lib/maldivesCharter/classicRewardPricing'
import RewardBundlePriceDisplay from './RewardBundlePriceDisplay'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getFeederOriginAirportCode } from 'lib/charterFlights/charterBundleOfferPageUtils'

const StyledNavWrapper = styled(NavWrapper)`
  align-items: center;
`

const ContentWrapper = styled(Group)`
  ${mediaQueryOnly.tablet} {
    padding-top: ${rem(16)};
  }
`

const PriceSummarySection = styled(CSSBreakpoint)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: auto;
  }

  ${mediaQueryUp.desktop} {
    padding-inline-start: ${rem(12)};
    justify-content: flex-end;
    margin-inline-start: auto;
  }

  > * + * {
    margin-inline-start: ${rem(12)};
  }
`

const PriceSummary = styled(Group)`
  position: relative;
`

const defaultMultiRoomPackageTotals: MultiRoomPackageTotals = {
  hotelPrice: 0,
  hotelMemberPrice: 0,
  hotelPlusFlightsPrice: 0,
  hotelPlusFlightsMemberPrice: 0,
  flightPrice: 0,
  hotelValue: 0,
}

export interface FlashHotelStickyHeaderProps {
  offer: App.Offer
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
  cheapestSelectedOption: App.OfferPackageOptionView
  className?: string
  durationLabel: string
  availableForPurchase?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
  rooms: Array<OfferPageRoom>
  isMultiRoom: boolean
  multiRoomPackageTotals: MultiRoomPackageTotals
  fareClass: FareClassWithReward
  flightOrigin?: App.Airport
  /** Page-level check-in (from URL/state). Used to decide whether to fall back to the calendar
   *  bundle floor for the "from" price (matches the summary/offer-tile behaviour). */
  checkInDate?: string
}

export function FlashHotelStickyHeader(props: PropsWithChildren<FlashHotelStickyHeaderProps>) {
  const {
    offer,
    onViewPackageOptions,
    children,
    calendarsLoading,
    className,
    durationLabel,
    cheapestSelectedOption: packageView,
    availableForPurchase,
    charterBundleRoute,
    priceMode,
    rooms,
    isMultiRoom,
    multiRoomPackageTotals,
    fareClass,
    flightOrigin,
    checkInDate,
  } = props

  const intl = useIntl()
  const {
    pricing: hotelPricing,
    memberPricing: hotelMemberPricing,
  } = packageView

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const dispatch = useOfferPageDispatch()

  const isWePriceMatchEnabled = !charterBundleRoute

  const onViewOptionsClick = () => {
    trackOfferPageTopCtaClick()
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }

  const showPriceWithFlights = !!(offer.bundledWithFlightsOnly || charterBundleRoute)

  // Charter bundle fallback pricing (mirrors FlashOfferSummary / SuggestedOptionPreview).
  // When no check-in is selected (or it doesn't match a charter date), the page-level flight
  // price hook returns the search-response's metadata aggregate, which is NOT Friends & Family
  // aware. Routing through `useCharterBundleFallbackPricing` instead picks the per-date
  // calendar floor — built by `useFlightItemData` which applies FNF when both legs are
  // eligible — so this sticky shows the same "from" price as the summary/offer-tile.
  const stickyNights = packageView.package.duration
  // Both hooks take the base cabin (cash) — reward variants share dates/journey data with
  // their base cabin so the date / fallback bundle is identical.
  const baseFareClassForFallback = toBaseFareClass(fareClass)
  const feederOriginAirportCode = getFeederOriginAirportCode(charterBundleRoute, flightOrigin)
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate,
    nights: stickyNights,
    charterBundleRoute,
    rooms,
    fareClass: baseFareClassForFallback,
    feederOriginAirportCode,
  })
  const isCheckInMatchingCharterDate = !!checkInDate && !!charterDepartDate
  const shouldUseFallbackPricing = !!charterBundleRoute && (!checkInDate || !isCheckInMatchingCharterDate)
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass: baseFareClassForFallback,
    nights: stickyNights,
    disabled: !shouldUseFallbackPricing,
    feederOriginAirportCode,
  })

  const [pricingToDisplay, memberPricingToDisplay] = useMemo(() => {
    let pricingToDisplay: App.PricingWithValue | undefined = hotelPricing
    let memberPricingToDisplay: App.PricingWithValue | undefined = hotelMemberPricing
    if (showPriceWithFlights) {
      if (shouldUseFallbackPricing && fallbackPricing) {
        // Calendar-derived bundle floor (FNF-aware). Already includes flight, don't add again.
        pricingToDisplay = fallbackPricing
        memberPricingToDisplay = fallbackMemberPricing
      } else if (flightPrice) {
        pricingToDisplay = addFlightPrice(pricingToDisplay, flightPrice)
        memberPricingToDisplay = memberPricingToDisplay && addFlightPrice(memberPricingToDisplay, flightPrice)
      } else {
        pricingToDisplay = undefined
        memberPricingToDisplay = undefined
      }
    }
    return [pricingToDisplay, memberPricingToDisplay]
  }, [hotelPricing, hotelMemberPricing, flightPrice, showPriceWithFlights, shouldUseFallbackPricing, fallbackPricing, fallbackMemberPricing])

  const hasMultiRoomHotelMemberPrice = multiRoomPackageTotals.hotelMemberPrice > 0
  const hasMultiRoomFlightMemberPrice = multiRoomPackageTotals.hotelPlusFlightsMemberPrice > 0
  const rawPrice = isMultiRoom
    ? showPriceWithFlights
      ? (flightPrice ? multiRoomPackageTotals.hotelPrice + flightPrice : multiRoomPackageTotals.hotelPlusFlightsPrice ?? undefined)
      : multiRoomPackageTotals.hotelPrice
    : pricingToDisplay?.price
  const rawMemberPrice = isMultiRoom
    ? showPriceWithFlights
      ? (flightPrice
          ? (hasMultiRoomHotelMemberPrice ? multiRoomPackageTotals.hotelMemberPrice + flightPrice : undefined)
          : (hasMultiRoomFlightMemberPrice ? multiRoomPackageTotals.hotelPlusFlightsMemberPrice : undefined))
      : (hasMultiRoomHotelMemberPrice ? multiRoomPackageTotals.hotelMemberPrice : undefined)
    : memberPricingToDisplay?.price

  const saleUnit = offer.bundledWithFlightsOnly ? 'package*' : isMultiRoom ? 'total' : offer.saleUnit

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)
  const displayPrice = typeof rawPrice === 'number'
    ? convertToDisplay(rawPrice)
    : undefined
  const displayMemberPrice = typeof rawMemberPrice === 'number'
    ? convertToDisplay(rawMemberPrice)
    : undefined
  const showMemberPrice = typeof displayMemberPrice === 'number' && displayMemberPrice > 0
  const rewardSaleUnit = priceMode === 'perPerson'
    ? intl.formatMessage({ id: 'hiXHHMWHZKBMB5g12zFn', defaultMessage: 'person', description: 'Per-person pricing unit suffix for reward price in the sticky header' })
    : saleUnit

  // Reward pricing — reuse the page-level flight price which already has dates and fare class
  const isRewardSelected = isRewardFareClass(fareClass)
  const { rewardSeatPointsTotal, rewardSeatCashCoPayTotal, rewardAvailable, rewardPreviewRate } = useOfferPageFlightPrice()
  const rewardPrice = getRewardPriceDisplayState({ rewardAvailable, rewardSeatPointsTotal, rewardSeatCashCoPayTotal })
  const showRewardPrice = isRewardSelected && rewardPrice.show && typeof rawPrice === 'number'
  const needsRewardPreview = isRewardSelected && !showRewardPrice

  const totalGuests = useMemo(() => Math.max(1, sumUpOccupancies(rooms)), [rooms])

  const rewardPreview = useMemo(() => {
    if (!needsRewardPreview) return undefined
    const rate = rewardPreviewRate
    if (!rate) return undefined
    const hotelPrice = isMultiRoom ? multiRoomPackageTotals.hotelPrice : hotelPricing.price
    const hotelMemberPrice = isMultiRoom
      ? (multiRoomPackageTotals.hotelMemberPrice > 0 ? multiRoomPackageTotals.hotelMemberPrice : undefined)
      : hotelMemberPricing?.price
    const pts = rate.pointsPerPerson * totalGuests
    const cash = hotelPrice + rate.cashPerPerson * totalGuests
    const memberCash = hotelMemberPrice ? hotelMemberPrice + rate.cashPerPerson * totalGuests : undefined
    return { points: pts, cash, memberCash }
  }, [needsRewardPreview, rewardPreviewRate, isMultiRoom, multiRoomPackageTotals.hotelPrice, multiRoomPackageTotals.hotelMemberPrice, hotelPricing.price, hotelMemberPricing?.price, totalGuests])

  const rewardPriceData = useMemo(() => {
    if (!showRewardPrice || rawPrice == null) return undefined
    const hotelPrice = rawPrice - (flightPrice ?? 0)
    const hotelMemberPrice = rawMemberPrice != null ? rawMemberPrice - (flightPrice ?? 0) : undefined
    return computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice,
      rewardPointsTotal: rewardSeatPointsTotal ?? 0,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal ?? 0,
    })
  }, [showRewardPrice, rawPrice, rawMemberPrice, flightPrice, rewardSeatPointsTotal, rewardSeatCashCoPayTotal])

  return (
    <StyledNavWrapper className={className}>
      <LayoutContainer>
        <ContentWrapper
          direction="horizontal"
          tabletDirection="vertical"
          desktopVerticalAlign="center"
          desktopDirection="horizontal"
          desktopHorizontalAlign="space-between"
          tabletGap={16}
        >
          {children}
          <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
            <PriceSummarySection>
              <PriceSummary direction="vertical" horizontalAlign="start" desktopHorizontalAlign="end">
                <LoadingIndicator visible={calendarsLoading} floating opaque />
                {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <BodyText variant="large" weight="bold">
                  <FormattedMessage
                    id="DmAweJrnchvqJIKE9J-t"
                    defaultMessage="This offer has<br></br> SOLD OUT"
                    description="Message shown when a flash hotel offer has sold out"
                    values={{
                      br: () => <CSSBreakpoint as="br" max="mobile" />,
                    }}
                  />
                </BodyText>}
                {availableForPurchase && rewardPriceData && <>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <RewardBundlePriceDisplay
                    points={convertToDisplay(rewardPriceData.points)}
                    cash={convertToDisplay(rewardPriceData.memberCash ?? rewardPriceData.cash)}
                    unitLabel={rewardSaleUnit}
                    size="S"
                  />
                </>}
                {availableForPurchase && !rewardPriceData && needsRewardPreview && rewardPreview && <>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <RewardBundlePriceDisplay
                    points={convertToDisplay(rewardPreview.points)}
                    cash={convertToDisplay(rewardPreview.memberCash ?? rewardPreview.cash)}
                    unitLabel={rewardSaleUnit}
                    size="S"
                  />
                </>}
                {availableForPurchase && !rewardPriceData && !(needsRewardPreview && rewardPreview) && typeof displayPrice === 'number' && <>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceGuard
                    showMemberPrice={showMemberPrice}
                    fallbackPrice={<>
                      <PriceRowPrice
                        size="S"
                        price={displayPrice}
                        saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                        data-testid="flash-hotel-sticky-header-price"
                      />
                      {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
                    </>}
                  >
                    <PriceRowLuxPlusPrice
                      size="M"
                      memberPrice={displayMemberPrice ?? 0}
                      saleUnit={priceMode === 'perPerson' ? 'person' : saleUnit}
                      logoPlacement="leading"
                    />
                    {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
                  </LuxPlusPriceGuard>
                </>}
              </PriceSummary>
              {!availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED && <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
                {intl.formatMessage(commonMessages.browseAllHotelOffers)}
              </TextButton>}
              {availableForPurchase &&
                <TextButton kind="primary" onClick={onViewOptionsClick}>
                  <CSSBreakpoint max="tablet" as="span">
                    {intl.formatMessage({
                      id: 'T9RQ708QxRYahM5DFr6V',
                      defaultMessage: 'View options',
                      description: 'Action button label to view options on mobile on the offer detail page',
                    })}
                  </CSSBreakpoint>
                  <CSSBreakpoint min="desktop" as="span">
                    {intl.formatMessage({
                      id: 'TGLQTsAtDsR9B3EzmY_w',
                      defaultMessage: 'View package options',
                      description: 'Action button label to view package options on desktop',
                    })}
                  </CSSBreakpoint>
                </TextButton>}
            </PriceSummarySection>
          </WalledContent>
        </ContentWrapper>
      </LayoutContainer>
    </StyledNavWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
    calendarsLoading: arePricesLoading(appState, state),
    durationLabel: getFromDurationLabel(state, appState),
    availableForPurchase: isAvailableForPurchase(state, appState),
    open: !!state.activeFilter,
    charterBundleRoute: state.charterBundleRoute,
    priceMode: state.priceMode,
    rooms: state.rooms,
    isMultiRoom,
    multiRoomPackageTotals: isMultiRoom ? getMultiRoomPackageTotals(state, appState) : defaultMultiRoomPackageTotals,
    fareClass: state.fareClass,
    flightOrigin: state.airport,
    checkInDate: state.checkInDate,
  }
}, FlashHotelStickyHeader, preventRenderWithOpenFilter)
